import { InventoryWebsiteVisibilityComponent } from '../../inventory-website-visibility.component';
import { inventoryCostingSummary, inventoryCostingDisplay, defaultCostingDisplayMode, CostingDisplayMode } from './costing-summary';
import { InventoryFamilyTreeComponent } from './inventory-family-tree/inventory-family-tree.component';
import { CreateTransferComponent, TransferPulldownResult } from '../../inventory-transfer/create-transfer/create-transfer.component';
import { RmsPulldownService } from '@services';
import { Component, Input, Output, OnInit, EventEmitter, ViewChild } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, TableIcon, InboundTallyData, HeaderButton, PromptButton } from '@interfaces';
import { RmsUploadService, PopupService, WebService, lastValueFrom, InventoryDataService, PromptService, RmsMessageService, ExcelWriterService  } from "@services";
import { TrafficNotesComponent } from './traffic-notes/traffic-notes.component';
import { TallyCreatorComponent } from './tally-creator/tally-creator.component';
import { InventoryChildActionsComponent } from './inventory-child-actions/inventory-child-actions.component';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { RmsFileExplorerComponent } from '../../../../../custom-modules/rms-module/rms-file-explorer/rms-file-explorer.component';
import { CostAdjustmentsComponent } from './cost-adjustments/cost-adjustments.component';//4.7

function getExtWithDot(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}
function fileWithoutExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(0, i) : name;
}
function parseTareWeights(input: string) {
  return input
    .split(', ')
    .map((amount, index) => ({
      TareWeight: Number(amount),
      BagNumber: index + 1
    }));
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[\\\/:\*\?"<>\|]+/g, ' - ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

type DocItem = {
  id: number;
  label: string;
  fileName: string;
};

@Component({
  selector: 'app-inventory-data',
  imports: [InventoryWebsiteVisibilityComponent, RmsModule, TallyCreatorComponent, CostAdjustmentsComponent, InventoryChildActionsComponent, InventoryFamilyTreeComponent],
  templateUrl: './inventory-data.component.html',
  styleUrl: './inventory-data.component.scss'
})
export class InventoryDataComponent implements OnInit {
  private costingChoice: {inventoryID:number,mode:CostingDisplayMode} | null = null;
  get hasCostingDifferential(): boolean { return inventoryCostingSummary(this.InventoryItem).differential !== null; }
  get costingMode():CostingDisplayMode { if (!this.hasCostingDifferential) return 'fixed'; return this.costingChoice && this.costingChoice.inventoryID === this.InventoryItem?.InventoryID ? this.costingChoice!.mode : defaultCostingDisplayMode(this.InventoryItem); }
  setCostingMode(mode:CostingDisplayMode) { this.costingChoice={inventoryID:this.InventoryItem.InventoryID,mode}; }
  get costingSummary() { return inventoryCostingDisplay(this.InventoryItem,this.costingMode); }
  costingStage: 'landed'|'aged'|'final' = 'landed';
  displayCost(value:number|null):string {
    return value==null?'—':new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',minimumFractionDigits:4,maximumFractionDigits:4}).format(value);
  }
  @Input() InventoryItem: any | null = null;
  @Output() onReturnToList = new EventEmitter<void>();
  @Output() onInventoryUpdated = new EventEmitter<void | number>();
  @ViewChild(TallyCreatorComponent) tallyCreatorRef?: TallyCreatorComponent;
  @ViewChild(RmsFileExplorerComponent) fileExplorer?: RmsFileExplorerComponent;
  @ViewChild(InventoryChildActionsComponent) childActions?: InventoryChildActionsComponent;
  @ViewChild(InventoryFamilyTreeComponent) familyTree?: InventoryFamilyTreeComponent;

  PrimaryFlavorNote: any | null = null;
  SecondaryFlavorNotes: any[] = [];
  MatchedFlavorNotes: any[] = [];
  costAdjustTemp: number = 0.37;
  costAdjustNotesTemp: string = "Cost adjusted due to quality issues.";
  finalizeData: { BagsExpected: number, WeightExpected: number, BagsWeighed: number, TotalWeight: number } | null = null;
  tallyData: any;
  InventoryDetails: any;
  InventoryData: any;
  CreatingTally: boolean = false;
  selectedView: { value: number; label: string } = { value: 1, label: "Inventory Documents" };
  TallySheetPath: any = null;
  FinalizeInboundView: boolean = false;
  // Set only right after creating a slack bag/22lb box/SKU conversion; drives which "ordered"
  // field shows (Box vs Slack Weight vs Bags Reconciled) instead of the generic
  // Bags Ordered/Net Weight pair.
  ChildKind: 'Slack' | '22lb Box' | 'Convert SKU' | null = this.InventoryItem?.ChildKind ?? null;

  InventoryReports: any[] = [
    { label: 'Commercial Invoice By PO' },
    { label: 'Commercial Invoice By Ref #' },
    { label: 'Commercial D/O By PO' },
    { label: 'Commercial D/O By Ref #' },
    { label: 'Inbound Report By PO' },
    { label: 'Inbound Report By Reff #' }
  ]
  HeaderButtons: HeaderButton[] = [
    {
      label: 'Change Table (Viewing: ' + this.selectedView.label + ')', menu:
        [
          { onAction: () => { this.selectedView = { value: 1, label: "Inventory Documents" } }, label: "Inventory Documents" },
          { onAction: () => { this.selectedView = { value: 2, label: "Purchase Order Documents" } }, label: "Purchase Order Documents" }
        ]
    }
  ]
  PurchaseOrderDocumentColumns: Column[] = [
    {label: "Ref #", key: 'ref'},
    { label: "Document Name", key: 'name' }
  ]
  InventoryDocumentColumns: Column[] = [
    { label: "Document Name", key: 'name' },
  ]
  PurchaseOrderDocumentIcons: TableIcon[] = [
    { icon: 'preview', action: (row) => { this.previewDocument = true; this.previewDocumentPath = row.path }, title: "View Document" }
  ]
  InventoryDocumentIcons: TableIcon[] = [
    { icon: 'preview', action: (row) => { this.previewDocument = true; this.previewDocumentPath = row.path }, title: "View Document" }
  ]
  PurchaseOrderDocuments: any[] = []
  InventoryDocuments: any[] = []
  previewHtml: string = '';
  viewingHTML: boolean = false;
  previewDocument: boolean = false;
  previewDocumentPath: string = '';
  existingTally?: InboundTallyData;
  poInventoryRootPath: string = '';
  FlavorNotes: any[] = [];
  constructor(private uploadService: RmsUploadService,
    private webService: WebService,
    private inventorydata: InventoryDataService,
    private prompt: PromptService,
    private message: RmsMessageService,
    private popup: PopupService,
    private excelsvc: ExcelWriterService,
    private pulldown: RmsPulldownService)
  {}

  private readonly docLabels: string[] = [
    'Organic Certificate',
    'Bill Of Lading',
    'Delivery Order',
    'Packing List',
    'SMBC Certificate',
    'RFA Certificate',
    'ICO',
    'PHYTO',
    'COA',
    'Fumigation',
    'Warehouse Tally',
    'Supplier Invoice',
    '7501 Form',
    'COO',
    'Weight Certificate',
    'Certificate Of Quality',
    'ACE Release',
    'ISF Form',
    'OBL',
    'SWB',
    'NOP'
  ];
  // -----------------------------------------
  // BASE mandatory lists (no conditions yet)
  // -----------------------------------------
  private mandatoryFOBBase: string[] = [
    'INVOICE',
    'Bill Of Lading',
    'PHYTO',
    'ICO',
    'Organic Certificate',
    'COA',
    'SMBC Certificate',
    'RFA Certificate',
    'OTC'
  ];

  private mandatoryEXWHSEBase: string[] = [
    'INVOICE',
    'SA',
    'PHYTO',
    'ICO',
    'Organic Certificate',
    'COA',
    'SMBC Certificate',
    'RFA Certificate',
    'Delivery Order',
    'RELEASE ORDER'
  ];

  // -----------------------------------------
  // CONDITIONAL CHECK HELPERS
  // -----------------------------------------
  private isOrganic(): boolean {
    return this.InventoryItem?.InventoryTypeName?.toLowerCase().includes('organic');
  }

  private isDecaf(): boolean {
    return this.InventoryItem?.InventoryTypeName?.toLowerCase().includes('decaf');
  }

  private isMXSW(): boolean {
    const name = this.InventoryItem?.InventoryTypeName?.toLowerCase() || '';
    return name.includes('mx') || name.includes('sw');
  }

  private isFOB(): boolean {
    return this.InventoryItem?.ShipmentTypeID === 1;
  }

  private isEXWHSE(): boolean {
    return this.InventoryItem?.ShipmentTypeID === 2;
  }

  private isSPOT(): boolean {
    return this.InventoryItem?.ShippingTerm === 4;
  }

  // -----------------------------------------
  // Build the final mandatory list dynamically
  // -----------------------------------------
  private getBaseMandatoryList(): string[] {
    if (this.isFOB()) return this.mandatoryFOBBase;
    if (this.isEXWHSE() || this.isSPOT()) return this.mandatoryEXWHSEBase;
    return [];
  }

  private applyConditions(list: string[]): string[] {
    const output = new Set<string>();

    for (const item of list) {
      const key = item.toUpperCase();

      if (key === 'ORGANIC CERTIFICATE') {
        if (this.isOrganic()) output.add('Organic Certificate');
        continue;
      }

      if (key === 'COA') {
        if (this.isDecaf()) output.add('COA');
        continue;
      }

      if (key === 'SMBC CERTIFICATE') {
        if (this.InventoryItem?.SMBC) output.add('SMBC Certificate');
        continue;
      }

      if (key === 'RFA CERTIFICATE') {
        if (this.InventoryItem?.RainforestAlliance) output.add('RFA Certificate');
        continue;
      }

      if (key === 'OTC') {
        if (this.isMXSW() && this.isDecaf()) output.add('OTC');
        continue;
      }

      if (key === 'DELIVERY ORDER') {
        if (this.isSPOT()) output.add('Delivery Order');
        continue;
      }

      if (key === 'RELEASE ORDER') {
        if (this.isEXWHSE()) output.add('RELEASE ORDER');
        continue;
      }

      // Always-required documents (Invoice, PHYTO, ICO, etc.)
      output.add(item);
    }

    return Array.from(output);
  }

  // -----------------------------------------
  // FINAL: Mandatory Documents Getter
  // -----------------------------------------
  get mandatoryDocs(): DocItem[] {
    if (!this.InventoryItem) return [];

    const baseList = this.getBaseMandatoryList();
    const conditioned = this.applyConditions(baseList);

    return this.docs.filter(d =>
      conditioned.some(m =>
        d.label.toLowerCase().includes(m.toLowerCase())
      )
    );
  }

  // -----------------------------------------
  // Supporting Docs
  // -----------------------------------------
  get supportingDocs(): DocItem[] {
    const mandatoryNames = new Set(this.mandatoryDocs.map(d => d.label));
    return this.docs.filter(d => !mandatoryNames.has(d.label));
  }
  docs: DocItem[] = [];

  /** Recompute docs when IDs are known/changed */
  private computeDocs(): void {
    const po = this.InventoryItem.PurchaseOrderID ?? '';
    const inv = this.InventoryItem.InventoryID ?? '';
    this.docs = this.docLabels.map((label, idx) => {
      const fn = this.buildFileName(po, inv, label);

      return { id: idx + 1, label, fileName: fn};
    });
  }

  /** Builds "<PO> - <INV> - <Label>" and sanitizes illegal filename characters */
  private buildFileName(po: string, inv: string, label: string): string {
    const raw = `${po} - ${inv} - ${label}`;
    return this.sanitizeFilename(raw);
  }
  haveDoc(doc: DocItem): boolean {
    return this.InventoryDocuments.some(d => fileWithoutExtension(d.name) === doc.label) || this.PurchaseOrderDocuments.some(e => fileWithoutExtension(e.name) === doc.label);
  }
  /** Remove chars invalid on Windows/macOS filesystems and normalize whitespace */
  private sanitizeFilename(name: string): string {
    // Replace \ / : * ? " < > | with hyphen, collapse spaces, and trim
    const cleaned = name
      .replace(/[\\\/:\*\?"<>\|]+/g, ' - ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return cleaned;
  }

  get HeaderButtonsBound(): HeaderButton[] {
    this.HeaderButtons[0].label = `Change Table (Viewing: ${this.selectedView.label})`;
    return this.HeaderButtons;
  }
  async ngOnInit() {
    await this.refreshCostingFields(false);
    this.ChildKind = null;
    this.FlavorNotes = await lastValueFrom(this.webService.getWebCall('files', `/list?path=${encodeURIComponent('/flavor_notes')}`))
    this.InventoryData = await this.inventorydata.getData({ keys: "ALL", params: { Truckers: { isActive: true } } });
    await this.loadInventoryItemData();
  }

  // Item-specific portion of ngOnInit (docs, flavor notes, cupping) — reusable without
  // re-fetching the static reference data (products, ports, etc.) that ngOnInit also loads.
  private async loadInventoryItemData(): Promise<void> {
    await this.ensureInventoryTypeName();
    const inventoryFilePath = `purchase-orders/po-${this.InventoryItem.PurchaseOrderID}`;
    const inventoryFolder = `reff-${this.InventoryItem.InventoryID}/`
    const poFilePath = `purchase-orders`;
    const poFolder = `po-${this.InventoryItem.PurchaseOrderID}`
    const cuppingSessions = JSON.parse(this.InventoryItem?.CuppingSessionDetailsJson || '[]');
    const flavorNotes = JSON.parse(this.InventoryItem?.InventoryFlavorNotesJson || '[]');
    this.PurchaseOrderDocuments = await lastValueFrom(this.webService.getWebCall('files', 'listByPathAndFolder', { params: [{ name: 'path', value: poFilePath }, { name: 'folderName', value: poFolder }] }))
    this.InventoryDocuments = await lastValueFrom(this.webService.getWebCall('files', 'listByPathAndFolder', { params: [{ name: 'path', value: inventoryFilePath }, { name: 'folderName', value: inventoryFolder }] }))
    this.poInventoryRootPath = inventoryFilePath;
    this.computeDocs();
    this.InventoryItem.PreshipCupping = cuppingSessions.find((s: any) => s.PreShip && !s.Recup) || null;
    this.InventoryItem.ArrivalCupping = cuppingSessions.find((s: any) => !s.PreShip && !s.Recup) || null;
    this.InventoryItem.FlavorNotes = flavorNotes.length > 0 ? flavorNotes : null;
    this.InventoryItem.TimesPreshipRecupped = cuppingSessions.filter((s: any) => s.PreShip && s.Recup).length;
    this.InventoryItem.TimesArrivalRecupped = cuppingSessions.filter((s: any) => !s.PreShip && s.Recup).length;
    this.buildFlavorNoteImages();
  }

  // get_inventory (used to reload a ref outside the grid, e.g. a child ref) doesn't return
  // InventoryTypeName, only InventoryTypeID. Look the name up directly rather than showing blank.
  private async ensureInventoryTypeName(): Promise<void> {
    if (this.InventoryItem?.InventoryTypeName || !this.InventoryItem?.InventoryTypeID) return;
    try {
      const types: any[] = await lastValueFrom(
        this.webService.getWebCall('inventory', `coffee/inventory-types/${this.InventoryItem.InventoryTypeID}`)
      );
      if (types?.[0]?.InventoryTypeName) {
        this.InventoryItem.InventoryTypeName = types[0].InventoryTypeName;
      }
    } catch (err) {
      console.error('Failed to look up InventoryTypeName', err);
    }
  }

  async refreshCostingFields(notifyParent = true) {
    if (notifyParent) this.onInventoryUpdated.emit(this.InventoryItem.InventoryID);
    const requestedId = this.InventoryItem.InventoryID;
    const rows: any[] = await lastValueFrom(
      this.webService.getWebCall('inventory', 'coffee/inventory/inventory-data', {
        params: [{ name: 'InventoryID', value: requestedId }]
      })
    );
    // Bail out if the user has since navigated to a different ref# while this was in flight
    if (this.InventoryItem.InventoryID !== requestedId) {
      return;
    }
    const fresh = rows?.[0];
    if (fresh) {
      Object.assign(this.InventoryItem, fresh);
    }
  }
  private buildFlavorNoteImages(): void {
    const selectedFlavorNotes = this.InventoryItem?.FlavorNotes || [];

    const flavorImageFiles = (this.FlavorNotes || []).map((file: any) => {
      const fileName = file.name || '';

      return {
        ...file,
        labelName: fileName.includes('.')
          ? fileName.substring(0, fileName.lastIndexOf('.'))
          : fileName
      };
    });

    this.MatchedFlavorNotes = selectedFlavorNotes
      .map((note: any) => {
        const matchedImage = flavorImageFiles.find((img: any) =>
          img.labelName?.trim().toLowerCase() ===
          note.FlavorNoteName?.trim().toLowerCase()
        );

        if (!matchedImage) return null;

        return {
          ...matchedImage,
          FlavorNoteID: note.FlavorNoteID,
          FlavorNoteName: note.FlavorNoteName,
          SessionSeqNum: Number(note.SessionSeqNum)
        };
      })
      .filter((x: any) => !!x)
      .sort((a: any, b: any) => a.SessionSeqNum - b.SessionSeqNum);

    this.PrimaryFlavorNote =
      this.MatchedFlavorNotes.find((n: any) => n.SessionSeqNum === 1) || null;

    this.SecondaryFlavorNotes =
      this.MatchedFlavorNotes.filter((n: any) => n.SessionSeqNum !== 1);
  }

  ReturnToList() {
    this.onReturnToList.emit();
  }
  async onChildUpdated() {
    this.onInventoryUpdated.emit(this.InventoryItem.InventoryID);
  }
  async onChildInventoryCreated(created: { InventoryID: number; label: 'Slack' | '22lb Box' | 'Convert SKU'; BagsOrdered: number; NetWeight: number; newTypeName?: string }) {
    try {
      this.onInventoryUpdated.emit(created.InventoryID);
      // const parentTypeName = this.InventoryItem.InventoryTypeName;
      // const detail: any[] = await lastValueFrom(
      //   this.webService.getWebCall('inventory', `coffee/inventory/${created.InventoryID}`)
      // );
      // if (!detail?.[0]) {
      //   this.message.error('Could not load the newly created ref#.');
      //   return;
      // }
      // this.InventoryItem = detail[0];
      // // get_inventory doesn't return InventoryTypeName or BagsOrdered; fill them from what we just created.
      // // A SKU conversion child has a different coffee type than the parent, so show the new type
      // // directly instead of "<parent type> (Convert SKU)".
      // this.InventoryItem.InventoryTypeName = created.newTypeName ?? `${parentTypeName} (${created.label})`;
      // this.InventoryItem.BagsOrdered = created.BagsOrdered;
      // this.ChildKind = created.label;
      // await this.loadInventoryItemData();
    } catch (err) {
      console.error('Failed to load newly created child inventory', err);
      this.message.error('Could not load the newly created ref#.');
    }
  }

  dataSwitcher(): any {
    return this.selectedView.value === 1 ? this.InventoryDocuments : this.PurchaseOrderDocuments;
  }
  columnSwitcher(): Column[] {
    return this.selectedView.value === 1 ? this.InventoryDocumentColumns : this.PurchaseOrderDocumentColumns;
  }
  iconSwitcher(): TableIcon[] {
    return this.selectedView.value === 1 ? this.InventoryDocumentIcons : this.PurchaseOrderDocumentIcons;
  }
  savingWebsiteVisibility = false;
  async setWebsiteInvisible(invisible: boolean): Promise<void> {
    if (this.savingWebsiteVisibility) return;
    const inventoryID = this.InventoryItem.InventoryID;
    this.savingWebsiteVisibility = true;
    try {
      const saved = await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory/website-visibility', {
        body: { InventoryID: inventoryID, WebInvisible: invisible }
      }));
      if (this.InventoryItem.InventoryID !== inventoryID) return;
      this.InventoryItem.WebInvisible = saved.WebInvisible;
      this.message.success(invisible ? 'Reference hidden from website.' : 'Reference visible on website.');
      this.onInventoryUpdated.emit(inventoryID);
    } catch { this.message.error('Unable to save website visibility. Please try again.'); }
    finally { this.savingWebsiteVisibility = false; }
  }
  async UpdateInventoryDetails() {
    const body = {
      InventoryID: this.InventoryItem.InventoryID,
      PurchaseOrderID: this.InventoryItem.PurchaseOrderID,
      ...this.InventoryItem
    }
    if (!this.InventoryItem.MultipleInventoryItems) {
      body.UpdatePurchaseOrder = true;
      await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory-detail/update', { body: body }))
      await this.ngOnInit();
      this.message.success("Successfully Updated Inventory Details");
      this.onInventoryUpdated.emit(this.InventoryItem.InventoryID);
      return;
    }
    const { closed$ } = this.prompt.open({
      promptText: "Update Ref#: " + this.InventoryItem.InventoryID + " or Purchase Order: " + this.InventoryItem.PurchaseOrderID + "?",
      buttons: [
        { label: "Ref#: " + this.InventoryItem.InventoryID, value: false, buttonClass: 'btn-bg-blue' },
        { label: "PO#: " + this.InventoryItem.PurchaseOrderID, value: true, buttonClass: 'btn-bg-blue' },
      ]
    })
    closed$.subscribe(async (result) => {
      if (result !== null) {
        body.UpdatePurchaseOrder = result;
        await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory-detail/update', { body: body }))
        await this.ngOnInit();
        this.message.success("Successfully Updated Inventory Details");
        this.onInventoryUpdated.emit(this.InventoryItem.InventoryID);
      }
      else {
        await this.ngOnInit();
      }
    })
  }
  onSampleDatesChanged() {
    if (!this.InventoryItem.MultipleInventoryItems) {
      this.UpdateSampleDates(true);
      return;
    }
    const { closed$ } = this.prompt.open({
      promptText: "Update Ref#: " + this.InventoryItem.InventoryID + " or Purchase Order: " + this.InventoryItem.PurchaseOrderID + "?",
      buttons: [
        { label: "Ref#: " + this.InventoryItem.InventoryID, value: false, buttonClass: 'btn-bg-blue' },
        { label: "PO#: " + this.InventoryItem.PurchaseOrderID, value: true, buttonClass: 'btn-bg-blue' },
      ]
    })
    closed$.subscribe(async (result) => {
      if (result !== null) {
        this.UpdateSampleDates(result);
      }
      else {
        await this.ngOnInit();
      }
    })
  }
  onTrafficDateChange() {
    let buttons = this.InventoryItem.MultipleInventoryItems ?
      ([{ label: "Ref#: " + this.InventoryItem.InventoryID, value: false, buttonClass: 'btn-bg-blue' },
        { label: "PO#: " + this.InventoryItem.PurchaseOrderID, value: true, buttonClass: 'btn-bg-blue' },]) :
      ([{ label: "Ref#: " + this.InventoryItem.InventoryID, value: false, buttonClass: 'btn-bg-blue' },])
    let promptText = this.InventoryItem.MultipleInventoryItems ? "Please add traffic note and select to update Reff: " + this.InventoryItem.InventoryID + " or Purchase Order: " + this.InventoryItem.PurchaseOrderID + "." :
      "Please add traffic note to update Reff: " + this.InventoryItem.InventoryID + "."
    const { closed$ } = this.prompt.open({
      promptText: promptText,
      buttons: buttons,
      promptInput: 'textarea',
      inputRequired: true,
      inputLabel: 'Traffic Note (Required)'
    })
    closed$.subscribe(async (result) => {
      if (result !== null) {
        this.UpdateTrafficDates(result.buttonValue, result.inputValue);
      }
      else {
        await this.ngOnInit();
      }
    })
  }
  async UpdateSampleDates(EntirePO: boolean) {
    const body = {
      ...this.InventoryItem,
      UpdatePurchaseOrder: EntirePO
    }
    try {
      await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory-dates/update', { body: body }));
      await this.ngOnInit();
      this.message.success("Successfully Updated Date")
      this.onInventoryUpdated.emit(this.InventoryItem.InventoryID);
    }
    catch (error) {
      this.message.error("Error Updating Date: " + error);
    }
  }
  async UpdateTrafficDates(EntirePO: boolean, TrafficNote: string) {
    const body = {
      ...this.InventoryItem,
      UpdatePurchaseOrder: EntirePO
    }
    const Notesbody = {
      InventoryID: this.InventoryItem.InventoryID,
      PurchaseOrderID: this.InventoryItem.PurchaseOrderID,
      TrafficNote: TrafficNote,
      EnteredBy: localStorage.getItem('displayName'),
      UpdatePurchaseOrder: EntirePO
    }
    try {
      await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory-dates/update', { body: body }));
      await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/traffic-notes/update', { body: Notesbody }));
      await this.ngOnInit();
      this.message.success("Successfully Updated Date and Added Traffic Note")
      this.onInventoryUpdated.emit(this.InventoryItem.InventoryID);
    }
    catch (err) {
      this.message.error("Error Updating Date or Adding Traffic Note: " + err);
    }

  }
  viewTrafficNotes() {
    this.popup.open({
      title: 'Traffic Notes',
      component: TrafficNotesComponent,
      data: {
        InventoryID: this.InventoryItem.InventoryID,
        MultipleInventoryItems: this.InventoryItem.MultipleInventoryItems,
        PurchaseOrderID: this.InventoryItem.PurchaseOrderID
      }
    })
  }
  //4.7.1 
  viewCostAdjustments() {
    this.popup.open({
      title: 'Cost Adjustments',
      component: CostAdjustmentsComponent,
      data: {
        InventoryID: this.InventoryItem.InventoryID
      }
    })
  }

  openTallyCreator() {
    this.tallyData = { ...this.InventoryItem };
    this.CreatingTally = true;
  }
  closeTallyCreator() {
    this.CreatingTally = false;
    this.tallyData = null;
    this.ngOnInit();
  }
  ContainsTallySheet() {

    const match = this.InventoryDocuments?.find(doc =>
      typeof doc.name === 'string' &&
      doc.name.toLowerCase().includes('tally')
    );

    if (match) {
      this.TallySheetPath = match.path;    // 👈 save the path
      return true;
    }

    this.TallySheetPath = null;
    return false;

  }


  async editTally() {
    try {
      if (!this.TallySheetPath) {
        this.message.error('No tally file found to edit.');
        return;
      }
      // 1) parse existing workbook -> data object
      this.existingTally = await this.excelsvc.ReadInboundTallyFromPath(this.TallySheetPath);
      console.log(this.existingTally);
      // 2) open creator with both the inventory context AND the parsed tally
      this.tallyData = { ...this.InventoryItem };     // your existing pattern
      this.CreatingTally = true;

      // If the child is not yet instantiated, rely on @Input binding in template.
      // If instantiated, you can also call:
      // setTimeout(() => this.tallyCreatorRef?.hydrateFromData(this.existingTally!), 0);
    } catch (err) {
      console.error('Edit Tally failed:', err);
      this.message.error('Could not open the existing tally for edit.');
    }
  }

  private normalizeWarehouseTallyData(rawData: any, warehouse: string):InboundTallyData {
    switch(warehouse) {
      case 'DupuyTally':
        // Normalize DupuyTally specific fields
        return {
          ...rawData,
          BagsExpected: Number(rawData.Bags),
          FullPalletWeights: [{ WeightWithPallets: Number(rawData.WeightWithPallet.replace(/,/g, '')), Bags: Number(rawData.Bags) }],
          Helper1: 'Dupuy',
          Helper2: 'Dupuy',
          HiLoDriver: 'Dupuy',
          Signature: 'Dupuy',
          StackingDriver: 'Dupuy',
          TareWeight: parseTareWeights(rawData.Tares),
          PalletWeight: Number(rawData.PalletWeight.replace(/,/g, '')),
          DateStripped: rawData.DateStripped ? new Date(rawData.DateStripped).toISOString() : undefined
        };
      case 'CostaOroTally':
        // Normalize CostaOroTally specific fields
        return {
          ...rawData,
          BagsExpected: Number(rawData.Bags),
          FullPalletWeights: [{ WeightWithPallets: Number(rawData.WeightWithPallet.replace(/,/g, '')), Bags: Number(rawData.Bags) }],
          Helper1: 'Costa Oro',
          Helper2: 'Costa Oro',
          HiLoDriver: 'Costa Oro',
          Signature: 'Costa Oro',
          StackingDriver: 'Costa Oro',
          TareWeight: [{TareWeight: Number(rawData.Tares.split('=')[1]),BagNumber: 1}],
          PalletWeight: Number(rawData.PalletWeight.replace(/,/g, '')),
          DateStripped: rawData.DateStripped ? new Date(rawData.DateStripped).toISOString() : undefined
        };
      case 'ContentnetalTally':
        // Normalize ContentnetalTally specific fields
        return {
          ...rawData,
          BagsExpected: Number(rawData.Bags),
          FullPalletWeights: [{ WeightWithPallets: Number(rawData.WeightWithPallet.replace(/,/g, '')), Bags: Number(rawData.Bags) }],
          Helper1: 'Contenental',
          Helper2: 'Contenental',
          HiLoDriver: 'Contenental',
          Signature: 'Contenental',
          StackingDriver: 'Contenental',
          TareWeight: [{ TareWeight: Number(rawData.Tares.split('=')[1]), BagNumber: 1 }],
          PalletWeight: Number(rawData.PalletWeight.replace(/,/g, '')),
          DateStripped: rawData.DateStripped ? new Date(rawData.DateStripped).toISOString() : undefined
        };
      default:
        throw new Error(`Unknown warehouse: ${warehouse}`);
    }
  }

  handleWeightCertUpload = async (file: File, _onProgress?: (p: number) => void) => {
    const { closed$ } = this.prompt.open({
      promptText: "What warheouse is this coming from?",
      buttons: [
        { label: "Dupuy", value: "DupuyTally", buttonClass: 'btn-bg-blue' },
        { label: "Costa Oro", value: "CostaOroTally", buttonClass: 'btn-bg-blue' },
        { label: "Contentnetal", value: "ContentnetalTally", buttonClass: 'btn-bg-blue' },
      ]
    })

    closed$.subscribe(async (result) => {
      if (!result) {
        this.message.error('Upload cancelled: No warehouse selected.');
        return
      }
      const form = new FormData();
      form.append('file', file);                        // <== the File from input
      form.append('modelId', result);     // or your custom model ID

      this.webService.getWebCall('ai', 'analyzeDI', { body: form })
        .subscribe({
          next: (res) => {
            console.log('DI result:', res);
            this.existingTally = this.normalizeWarehouseTallyData(res.byKey, result)
            this.tallyData = { ...this.InventoryItem };     // your existing pattern
            this.CreatingTally = true;
          },
          error: (err) => console.error('DI error', err)
        });
    })
  };
  extract(text: string, regex: RegExp): string {
    const m = text.match(regex);
    return m ? m[1] : "";
  }
 

  async UploadDocument(uploadData: any) {
    const files: File[] = uploadData.files;
    const path: string = uploadData.path;

    // Use your docs (with IDs or labels); here you used labels as values
    const availableDocs = this.docs.map(d => ({ label: d.label, value: d.label }));
    availableDocs.push({ label: 'Misc', value: 'Misc' });

    for (const file of files) {
      // 1) Ask for document type
      const { closed$ } = this.prompt.open({
        promptText: `What Type Of Document Are You Uploading? (${file.name})`,
        promptInput: 'select',
        inputLabel: 'Document Type',
        inputOptions: availableDocs,
        buttons: [
          { label: 'Save', value: true, buttonClass: 'btn-bg-green' }
        ]
      });

      const result: any = await firstValueFrom(closed$.pipe(take(1)));

      // Handle cancel / dismiss (result might be null) or Save=false
      if (!result || result.buttonValue === false) {
        this.message.error(`Document upload cancelled for ${file.name}.`);
        continue; // go to next file
      }

      // 2) Resolve base filename
      let baseName: string | undefined;

      if (result.inputValue === 'Misc') {
        const miscPrompt = this.prompt.open({
          promptText: `Please enter a file name for this document:${file.name}`,
          promptInput: 'text',
          inputLabel: 'File Name',
          inputRequired: true,
          buttons: [
            { label: 'Save', value: true, buttonClass: 'btn-bg-green' }
          ]
        });
        const miscResult: any = await firstValueFrom(miscPrompt.closed$.pipe(take(1)));
        if (!miscResult || miscResult.buttonValue === false || !miscResult.inputValue?.trim()) {
          this.message.error(`No filename entered for ${file.name}. Skipping.`);
          continue;
        }
        baseName = miscResult.inputValue.trim();
      } else {
        // You chose to use label as the file base; if you want your computed "<PO> - <INV> - Label", use this.docs lookup instead
        baseName = String(result.inputValue);
      }

      // 3) Build final name with extension + sanitize
      const extWithDot = getExtWithDot(file.name); // e.g., ".pdf"
      let finalName = sanitizeFilename(`${baseName}${extWithDot}`);

      // Optional: if you actually want "PO - INV - Label.ext", do:
      // const po = this.InventoryItem.PurchaseOrderID ?? '';
      // const inv = this.InventoryItem.InventoryID ?? '';
      // finalName = sanitizeFilename(`${po} - ${inv} - ${baseName}${extWithDot}`);

      // 4) Upload
      const form = new FormData();
      form.append('file', file);
      form.append('fileName', finalName);
      form.append('path', path);

      await lastValueFrom(
        this.webService.getWebCall('files', '/upload', { body: form })
        // If your service uses a POST wrapper, prefer that:
        // this.webService.postWebCall('files', '/upload', form)
      );

      this.message.success(`${finalName} uploaded successfully.`);
    }

    // Refresh once after all uploads finish
    await this.ngOnInit();
    this.fileExplorer?.refresh();
  }
  isManagement(): boolean {
    const user = localStorage['displayName'];
    return user === 'Michael Bray' || user === 'David Johnson' || user === 'Daniel Wojcik'
  }
  async StartFinalizeInbound() {
    try {
      if (!this.TallySheetPath) {
        this.message.error('No tally file found to edit.');
        return;
      }
      // 1) parse existing workbook -> data object
      const tally = await this.excelsvc.ReadInboundTallyFromPath(this.TallySheetPath);
      let bags = 0;
      let palletWeights = 0;

      tally.FullPalletWeights.forEach(w => {
        bags += w.Bags;
        palletWeights += w.WeightWithPallets;
      });

      this.finalizeData = {
        BagsExpected: tally.BagsExpected,
        WeightExpected: tally.BagsExpected * this.InventoryItem.NetWeight,
        BagsWeighed: bags,
        TotalWeight: palletWeights - tally.PalletWeight
      };
      this.FinalizeInboundView = true;
      

    } catch (err) {
      console.error('Edit Tally failed:', err);
      this.message.error('Could not open the existing tally for edit.');
    }
  }
  async FinalizeInbound() {
    if (this.finalizeData) {
      this.InventoryItem.BagsInbounded = this.finalizeData.BagsWeighed;
      this.InventoryItem.WeightReceived = this.finalizeData.TotalWeight
      const body = {
        InventoryID: this.InventoryItem.InventoryID,
        PurchaseOrderID: this.InventoryItem.PurchaseOrderID,
        UpdatePurchaseOrder: false,
        FinalPosition: 'INSTORE',
        ...this.InventoryItem
      }
      await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory-detail/update', { body: body }))
      await this.ngOnInit();
      this.message.success("Successfully Updated Inventory Details");
      this.onInventoryUpdated.emit(this.InventoryItem.InventoryID);
      this.finalizeData = null;
      this.FinalizeInboundView = false;
    }
  }
  OpenSlackDialog() {
    const { closed$ } = this.prompt.open({
      promptText: `Please enter weight of slack bag`,
      promptInput: 'text',
      inputLabel: 'Slack Weight (lbs)',
      inputRequired: true,
      buttons: [
        { label: 'Save', value: true, buttonClass: 'btn-bg-green' }
      ]
    })
  }
  Open22lbBoxDialog() {
    const { closed$ } = this.prompt.open({
      promptText: `Please enter number of boxes you want to create`,
      promptInput: 'text',
      inputLabel: 'Number of Boxes',
      inputRequired: true,
      buttons: [
        { label: 'Save', value: true, buttonClass: 'btn-bg-green' }
      ]
    })
  }
  
      // 4.6.1 - Quick setup: select "Transfer Inventory" from Actions, pick a destination,
      // enter bag count. Creates a child inventory row in the holding tank tied back to
      // this ref# via ParentInventoryID. The original PO/ref# is never modified (4.6.3).
     
  private transferOpening = false;
  async OpenInventoryTransferDialog() {
    if (this.transferOpening) return;
    this.transferOpening = true;
    const inventoryID = this.InventoryItem.InventoryID;
    try {
      const [destinations, sources] = await Promise.all([
        lastValueFrom(this.webService.getWebCall('inventory', 'coffee/inventory/transfer/destinations')),
        lastValueFrom(this.webService.getWebCall('inventory', 'coffee/inventory/available-for-transfer', {
          params: [{ name: 'InventoryID', value: inventoryID }]
        }))
      ]);
      if (!sources?.length) {
        this.message.error('This inventory reference could not be found. Refresh the screen and try again.');
        return;
      }
      const source = sources[0];
      if (source.TransferStatus === 'ON_CONTAINER') {
        this.message.error(`Ref #${inventoryID} is already assigned to outbound container${source.TransferContainerID ? ' #' + source.TransferContainerID : ''}. Open Inventory transfers to view or edit that transfer; it cannot be staged twice.`);
        return;
      }
      if (source.TransferStatus === 'HOLDING_TANK') {
        this.message.error(`Ref #${inventoryID} is already staged in the holding tank. Open Inventory transfers to edit the staged transfer or add it to a container.`);
        return;
      }
      if (!(Number(source.AvailableQty) > 0)) {
        this.message.error(`Ref #${inventoryID} has no unallocated bags available to transfer.`);
        return;
      }
      const ref = this.pulldown.open(CreateTransferComponent, {
        title: 'Create Transfer', width: '60vw', top: '0', maxHeight: '100vh', minHeight: '30vh',
        data: { destinations, availableInventoryOptions: sources, sourceInventoryID: inventoryID }
      });
      const result: TransferPulldownResult | null = await firstValueFrom(ref.afterClosed().pipe(take(1)));
      if (result?.action !== 'save') return;
      await lastValueFrom(this.webService.getWebCall('inventory', 'coffee/inventory/transfer/stage', {
        body: {
          InventoryID: inventoryID, createChild: true, Transfer: true,
          overrides: {
            FinalDestinationWarehouseID: result.destinationID, HoldingTankID: result.holdingTankID,
            TransferStatus: 'HOLDING_TANK', BagsOrdered: result.bagCount, BagsInbounded: null
          }
        }
      }));
      this.message.success(`${result.bagCount} bags moved to the holding tank.`);
      await this.ngOnInit();
      void this.familyTree?.load();
      this.onInventoryUpdated.emit(inventoryID);
    } catch (err: any) {
      this.message.error(err?.error?.error || 'Could not create the transfer. Please try again.');
    } finally { this.transferOpening = false; }
  }
}

